import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Observable } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionFormListBloc } from '@connect-services/client/blocs/field-intervention-form-list.bloc';
import { FieldInterventionLayerListBloc } from '@connect-services/client/blocs/field-intervention-layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { bypassDeactivateModal } from '@connect-services/client/guards/can-dismount.guard';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    FieldIntervention,
    FieldInterventionControl,
    FieldInterventionControlResult,
    FieldInterventionControlResultControllerGetRequestParams,
    FieldInterventionControlResultMetadataDto,
    FieldInterventionControlResultsService,
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionEntityControllerGet200Response,
    FieldInterventionExportDto,
    FieldInterventionForm,
    FieldInterventionFormsService,
    FieldInterventionsService,
    JsonForm,
    Layer,
    LayerWithAttributes,
    Person,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export type FieldInterventionControlResultDetails = FieldInterventionControlResult & {
    controlLabel: string;
    controlledByName: string;
    format: FieldInterventionControl.FormatEnum | null;
    hasControlResultAttentionPoint: boolean;
    severity: FieldInterventionControlResultMetadataDto.SeverityEnum;
};

type IconProperty = { backgroundColor?: string; color: string; icon: IconProp; tooltip?: string };

export type FieldInterventionContextLayerOption = SelectOption<string> & {
    layerIds: string[];
};

const fieldInterventionContextLayerPagination = {
    order: OrderEnum.ASC,
    orderBy: 'name',
    type: Layer.TypeEnum.TABLE,
};

@Injectable({
    providedIn: 'root',
})
export class FieldInterventionService {
    public static readonly entityStatuses: { [key: string]: string } = {
        [FieldInterventionEntity.StatusEnum.DONE]: 'validated',
        [FieldInterventionEntity.StatusEnum.INVALID]: 'invalidFieldInterventionEntity',
        [FieldInterventionEntity.StatusEnum.ONGOING]: 'ongoing',
        [FieldInterventionEntity.StatusEnum.PLANNED]: 'planned',
        [FieldInterventionEntity.StatusEnum.TODO]: 'toPlan',
        [FieldInterventionEntity.StatusEnum.TO_VALIDATE]: 'toValidate',
    };
    public static readonly entityStatusesIconsProperties: { [key: string]: IconProperty } = {
        [FieldInterventionEntity.StatusEnum.DONE]: {
            backgroundColor: 'bg-on-custom-green-v2',
            color: 'text-custom-green-v2',
            icon: ['fas', 'check'],
            tooltip: 'validated',
        },
        [FieldInterventionEntity.StatusEnum.INVALID]: {
            backgroundColor: 'bg-on-custom-red',
            color: 'text-custom-red',
            icon: ['fas', 'xmark'],
            tooltip: 'invalidFieldInterventionEntity',
        },
        [FieldInterventionEntity.StatusEnum.ONGOING]: {
            backgroundColor: 'bg-on-custom-yellow',
            color: 'text-custom-yellow',
            icon: ['fas', 'hourglass-half'],
            tooltip: 'ongoing',
        },
        [FieldInterventionEntity.StatusEnum.PLANNED]: {
            backgroundColor: 'bg-on-custom-purple',
            color: 'text-custom-purple',
            icon: ['fas', 'clock'],
            tooltip: 'planned',
        },
        [FieldInterventionEntity.StatusEnum.TODO]: {
            backgroundColor: 'bg-on-custom-turquoise',
            color: 'text-custom-turquoise',
            icon: ['fas', 'calendar-week'],
            tooltip: 'toPlan',
        },
        [FieldInterventionEntity.StatusEnum.TO_VALIDATE]: {
            backgroundColor: 'bg-on-custom-brown',
            color: 'text-custom-brown',
            icon: ['fas', 'business-time'],
            tooltip: 'toValidate',
        },
    };
    public static readonly statusesIconsProperties: { [key: string]: IconProperty } = {
        [FieldIntervention.StatusEnum.ONGOING]: {
            color: 'text-custom-orange',
            icon: ['fad', 'clock'],
        },
        [FieldIntervention.StatusEnum.DONE]: {
            color: 'text-custom-green',
            icon: ['fad', 'check-circle'],
        },
        [FieldIntervention.StatusEnum.TO_PLAN]: {
            color: 'text-on-custom-turquoise',
            icon: ['fas', 'calendar-week'],
        },
    };
    public readonly hasEntities: WritableSignal<boolean | null> = signal(null);
    public readonly isEntityResultDisplayed: WritableSignal<boolean> = signal(false);

    protected detailPanelOutletRegex: RegExp = /\/\(detailPanel:([a-zA-Z0-9-]+)/;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionControlResultsService = inject(FieldInterventionControlResultsService);
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionFormListBloc = inject(FieldInterventionFormListBloc);
    private readonly fieldInterventionFormsService = inject(FieldInterventionFormsService);
    private readonly fieldInterventionLayerListBloc = inject(FieldInterventionLayerListBloc);
    private readonly fieldInterventionServiceSdk = inject(FieldInterventionsService);
    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly layerService = inject(LayerService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public buildFieldInterventionControlResultDetails(
        controlResults: FieldInterventionControlResult[],
        controlResultsMetadata: FieldInterventionControlResultMetadataDto[],
        jsonForm: JsonForm,
        people: Person[],
    ): FieldInterventionControlResultDetails[] {
        const peopleById = new Map<string, Person>(
            people.map((person: Person): [string, Person] => [person.id, person]),
        );

        const metadataByControlResultId = new Map<string, FieldInterventionControlResultMetadataDto>(
            controlResultsMetadata.map(
                (
                    metadata: FieldInterventionControlResultMetadataDto,
                ): [string, FieldInterventionControlResultMetadataDto] => [metadata.controlResultId, metadata],
            ),
        );

        return controlResults.map(
            (controlResult: FieldInterventionControlResult): FieldInterventionControlResultDetails => {
                const controlSchema = jsonForm.jsonSchema.properties?.[controlResult.control];
                const controlledBy = peopleById.get(controlResult.controlledBy);
                const metadata = metadataByControlResultId.get(controlResult.id);
                const controlLabel = controlSchema?.title ?? controlSchema?.items?.title;

                AssertService.isString(controlLabel);

                if (!controlledBy) {
                    throw new Error(
                        `Person ${controlResult.controlledBy} not found for control result ${controlResult.id}`,
                    );
                }

                if (!metadata) {
                    throw new Error(`Metadata not found for control result ${controlResult.id}`);
                }

                return {
                    ...controlResult,
                    controlLabel,
                    controlledByName: `${controlledBy.givenName} ${controlledBy.familyName}`.trim(),
                    format: this.getControlFormat(controlSchema),
                    hasControlResultAttentionPoint: metadata.hasControlResultAttentionPoint,
                    severity: metadata.severity,
                };
            },
        );
    }

    public async export(fieldInterventionId: string, exportParams: FieldInterventionExportDto): Promise<void> {
        await firstValueFrom(
            this.fieldInterventionServiceSdk.fieldInterventionControllerExportFieldIntervention({
                fieldInterventionExportDto: exportParams,
                id: fieldInterventionId,
            }),
        );
    }

    public async fetchAllForms(): Promise<FieldInterventionForm[]> {
        const initialData = this.fieldInterventionFormListBloc.value()!;
        const forms: FieldInterventionForm[] = [...initialData.items];

        return forms.concat(
            await this.fetchAllPaginatedItems(
                this.fieldInterventionFormsService.fieldInterventionFormControllerGet.bind(
                    this.fieldInterventionFormsService,
                ),
                { ...defaultPaginationParameters, order: OrderEnum.ASC, orderBy: 'name', page: initialData.page + 1 },
            ),
        );
    }

    public async fetchAllLayersWithAttributes(): Promise<LayerWithAttributes[]> {
        const layersPagination = this.fieldInterventionLayerListBloc.value()!;
        const layers: LayerWithAttributes[] = [...layersPagination.items];
        let hasNextPage = layersPagination.hasNextPage;
        const paginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
            page: layersPagination.page + 1,
        };

        while (hasNextPage) {
            const paginatedLayersList = await firstValueFrom(
                this.fieldInterventionServiceSdk.fieldInterventionControllerGetLayersWithAttributes({
                    id: this.fieldInterventionBloc.value()!.id,
                    ...paginationParameters,
                }),
            );

            layers.push(...paginatedLayersList.items);
            hasNextPage = paginatedLayersList.hasNextPage;
            paginationParameters.page += 1;
        }

        return layers;
    }

    public async fetchAvailableFieldInterventionContextLayerOptions(): Promise<FieldInterventionContextLayerOption[]> {
        const allProjectLayersResponse = await this.layerService.getAllLayers(fieldInterventionContextLayerPagination);

        return this.mapLayersToContextLayerOptions(allProjectLayersResponse.items);
    }

    public async fetchContextLayerSelectionData(): Promise<{
        options: FieldInterventionContextLayerOption[];
        selectedOptions: FieldInterventionContextLayerOption[];
    }> {
        const [options, selectedOptions] = await Promise.all([
            this.fetchAvailableFieldInterventionContextLayerOptions(),
            this.fetchSelectedFieldInterventionContextLayerOptions(),
        ]);

        return {
            options,
            selectedOptions,
        };
    }

    public fetchFieldInterventionControlResultMetadata(
        fieldInterventionEntityId: string,
        fieldInterventionFormId: string,
    ): Promise<FieldInterventionControlResultMetadataDto[]> {
        return firstValueFrom(
            this.fieldInterventionControlResultsService.fieldInterventionControlResultControllerGetMetadata({
                entity: fieldInterventionEntityId,
                form: fieldInterventionFormId,
            }),
        );
    }

    public fetchFieldInterventionControlResultsByEntityAndForm(
        fieldInterventionEntityId: string,
        fieldInterventionFormId: string,
    ): Promise<FieldInterventionControlResult[]> {
        return this.fetchAllService.getAll<
            FieldInterventionControlResultControllerGetRequestParams,
            FieldInterventionControlResult
        >(
            this.fieldInterventionControlResultsService.fieldInterventionControlResultControllerGet.bind(
                this.fieldInterventionControlResultsService,
            ),
            {
                entity: fieldInterventionEntityId,
                form: fieldInterventionFormId,
            },
        );
    }

    public async fetchSelectedFieldInterventionContextLayerOptions(): Promise<FieldInterventionContextLayerOption[]> {
        const contextLayerIds = this.fieldInterventionBloc.value()?.contextLayer ?? [];

        if (!contextLayerIds.length) {
            return [];
        }

        const uniqueContextLayerIds = new Set(contextLayerIds);

        const allProjectLayersResponse = await this.layerService.getAllLayers(fieldInterventionContextLayerPagination);

        const selectedLayers = allProjectLayersResponse.items.filter((layer: Layer) =>
            uniqueContextLayerIds.has(layer.id),
        );

        return this.mapLayersToContextLayerOptions(selectedLayers);
    }

    public getDetailPanelUuidFromUrl(): string | null {
        const currentUrl = this.router.url;
        const match = currentUrl.match(this.detailPanelOutletRegex);

        return match ? match[1] : null;
    }

    public isNonApplicableForm(entity: FieldInterventionEntity, item: Person | FieldInterventionForm): boolean {
        if (!this.isFieldInterventionForm(item)) {
            return false;
        }

        if (!entity.notApplicableFormIds) {
            return false;
        }

        return entity.notApplicableFormIds.includes(item.id);
    }

    public mapContextLayerOptionsToLayerIds(selectedOptions: FieldInterventionContextLayerOption[]): string[] {
        return selectedOptions.flatMap((option: FieldInterventionContextLayerOption) => option.layerIds);
    }

    public openDeleteModal(): void {
        if (!this.fieldInterventionBloc.value()) {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('confirmDeleteIntervention'),
                title: this.translocoService.translate('warning'),
                type: ModalType.DANGER,
            },
            maxWidth: '30vw',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }
            await this.deleteFieldIntervention();
        });
    }

    public resetHasEntities(): void {
        this.hasEntities.set(null);
    }

    public async setHasEntities(fieldIntervention: FieldIntervention): Promise<void> {
        const fieldInterventionEntitiesResponse: FieldInterventionEntityControllerGet200Response = await firstValueFrom(
            this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGet({
                fieldIntervention: fieldIntervention.id,
                take: 1,
            }),
        );
        this.hasEntities.set(!!fieldInterventionEntitiesResponse?.items.length);
    }

    public async toggleFieldInterventionEntityDetailPanel(entityId: string): Promise<void> {
        const currentEntityId = this.getDetailPanelUuidFromUrl();
        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()?.id,
        ];
        const baseRouteParam = this.getBaseRouteParamFromUrl();
        if (baseRouteParam) {
            path.push(baseRouteParam);
        }

        const panelParam = currentEntityId && currentEntityId === entityId ? null : entityId;

        await this.router.navigate(
            [
                ...path,
                {
                    outlets: { detailPanel: panelParam },
                },
            ],
            {
                info: bypassDeactivateModal,
                queryParams: { ...this.activatedRoute.snapshot.queryParams },
                queryParamsHandling: 'merge',
            },
        );
    }

    public async updateFieldInterventionContextLayers(contextLayerIds: string[]): Promise<void> {
        const fieldIntervention = this.fieldInterventionBloc.value();
        if (!fieldIntervention) {
            return;
        }

        await firstValueFrom(
            this.fieldInterventionsService.fieldInterventionControllerPatch({
                id: fieldIntervention.id,
                updateFieldInterventionDto: {
                    contextLayer: contextLayerIds,
                },
            }),
        );

        await this.fieldInterventionBloc.load(
            {
                id: fieldIntervention.id,
            },
            { refresh: true },
        );
    }

    private buildContextLayerOptions(groupedLayers: Record<string, Layer[]>): FieldInterventionContextLayerOption[] {
        return Object.values(groupedLayers)
            .map((groupedLayer: Layer[]): FieldInterventionContextLayerOption => {
                const firstLayer = groupedLayer[0];

                return {
                    label: firstLayer.name,
                    layerIds: groupedLayer.map((layer: Layer) => layer.id),
                    value: this.normalizeLayerName(firstLayer.name),
                };
            })
            .sort((a: FieldInterventionContextLayerOption, b: FieldInterventionContextLayerOption) =>
                a.label.localeCompare(b.label),
            );
    }

    private async deleteFieldIntervention(): Promise<void> {
        try {
            await firstValueFrom(
                this.fieldInterventionsService.fieldInterventionControllerDelete({
                    id: this.fieldInterventionBloc.value()!.id,
                }),
            );

            this.snackBarService.openSuccessSnackBar('fieldInterventionDeleteSuccess');
            await this.router.navigate([
                RoutePathEnum.PROJECTS,
                this.projectBloc.value()!.id,
                RoutePathEnum.FIELD_INTERVENTIONS,
            ]);
        } catch {
            this.snackBarService.openErrorSnackBar('errorDELETE');
        }
    }

    private async fetchAllPaginatedItems<T, P extends PaginationParameters & Record<string, unknown>>(
        fetchFunction: (params: P) => Observable<{ hasNextPage: boolean; items: T[] }>,
        paginationParameters: P,
    ): Promise<T[]> {
        const allItems: T[] = [];
        let hasNextPage = true;

        while (hasNextPage) {
            const paginatedResponse = await firstValueFrom(fetchFunction({ ...paginationParameters }));
            allItems.push(...paginatedResponse.items);
            hasNextPage = paginatedResponse.hasNextPage;
            paginationParameters = { ...paginationParameters, page: paginationParameters.page + 1 };
        }

        return allItems;
    }

    private getBaseRouteParamFromUrl(): RoutePathEnum | undefined {
        let url = this.router.url;
        if (url.includes('?')) {
            url = url.substring(url.indexOf('?'), -1);
        }
        const urlSplit = url.split('/');

        let routeParam = urlSplit.pop();
        if (routeParam?.charAt(0) === '(') {
            routeParam = urlSplit.pop();
        }

        AssertService.isEnum(routeParam, RoutePathEnum);

        return routeParam;
    }

    private getControlFormat(controlSchema: {
        format?: string;
        items?: {
            format?: string;
        };
    }): FieldInterventionControl.FormatEnum | null {
        const format = controlSchema.format ?? controlSchema.items?.format;

        if (!format || !this.isControlFormat(format)) {
            return null;
        }

        return format;
    }

    private groupLayersByNormalizedName(layers: Layer[]): Record<string, Layer[]> {
        return layers.reduce((acc: Record<string, Layer[]>, layer: Layer) => {
            if (!layer.hasGeom) {
                return acc;
            }

            const normalizedName = this.normalizeLayerName(layer.name);
            if (!normalizedName) {
                return acc;
            }

            if (!acc[normalizedName]) {
                acc[normalizedName] = [];
            }

            acc[normalizedName].push(layer);

            return acc;
        }, {});
    }

    private isControlFormat(format: string): format is FieldInterventionControl.FormatEnum {
        return Object.values(FieldInterventionControl.FormatEnum).includes(
            format as FieldInterventionControl.FormatEnum,
        );
    }

    private isFieldInterventionForm(item: Person | FieldInterventionForm): item is FieldInterventionForm {
        return 'name' in item && 'id' in item;
    }

    private mapLayersToContextLayerOptions(layers: Layer[]): FieldInterventionContextLayerOption[] {
        const groupedLayers = this.groupLayersByNormalizedName(layers);

        return this.buildContextLayerOptions(groupedLayers);
    }

    private normalizeLayerName(layerName: string | null | undefined): string {
        return (layerName ?? '').trim().toLowerCase();
    }
}
